import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSiteMode } from "@/contexts/SiteModeContext";
import logo from "@/assets/Weautomate logo-01.png";

const HEX_POINTS = "50 2, 98 26, 98 74, 50 98, 2 74, 2 26";

function PulsingHex() {
  return (
    <div className="relative mx-auto w-48 h-48 mb-12">
      {[0, 1, 2].map((i) => (
        <motion.svg
          key={i}
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: [0, 0.3, 0], scale: [0.85, 1.4, 1.4] }}
          transition={{ duration: 2.4, delay: i * 0.8, repeat: Infinity, ease: "easeOut" }}
        >
          <polygon points={HEX_POINTS} fill="none"
                   stroke="url(#hex-grad)" strokeWidth="1.5" />
          <defs>
            <linearGradient id="hex-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#9D4CDD" />
              <stop offset="50%" stopColor="#FF3366" />
              <stop offset="100%" stopColor="#FF9900" />
            </linearGradient>
          </defs>
        </motion.svg>
      ))}
      <motion.svg
        viewBox="0 0 100 100"
        className="absolute inset-0 w-full h-full"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
      >
        <polygon points={HEX_POINTS} fill="none"
                 stroke="url(#hex-grad2)" strokeWidth="1.5" strokeDasharray="6 4" />
        <defs>
          <linearGradient id="hex-grad2" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9D4CDD" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FF3366" stopOpacity="0.4" />
          </linearGradient>
        </defs>
      </motion.svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <img src={logo} alt="WeAutomate" className="h-16 w-auto" />
      </div>
    </div>
  );
}

export default function ComingSoon() {
  const { modeData } = useSiteMode();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const notify = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { email, source: "coming_soon_notify" });
      setDone(true);
      toast.success("You're on the list.");
    } catch { toast.error("Could not save — try again."); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center relative overflow-hidden">
      {/* ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-r from-[#9D4CDD] via-[#FF3366] to-[#FF9900] opacity-[0.07] blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex flex-col items-center"
      >
        <PulsingHex />

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-5xl lg:text-7xl xl:text-8xl tracking-tighter gradient-text leading-tight max-w-3xl"
        >
          {modeData.title || "Something magic is being built."}
        </motion.h1>

        {modeData.message && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6 text-xl text-white/60 max-w-lg leading-relaxed"
          >
            {modeData.message}
          </motion.p>
        )}

        {!modeData.message && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6 text-lg text-white/50 max-w-md"
          >
            World-class product engineering and design, engineered in India.
            Leave your email and we'll let you know the moment we launch.
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-10 w-full max-w-md"
        >
          {done ? (
            <div className="glass rounded-2xl px-8 py-6">
              <div className="text-2xl font-display gradient-text">You're on the list.</div>
              <p className="mt-2 text-white/60 text-sm">We'll reach out the moment we're live.</p>
            </div>
          ) : (
            <form onSubmit={notify} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email" required placeholder="Your email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/5 border border-white/15 rounded-full px-6 py-3.5 outline-none focus:border-[#9D4CDD] text-sm placeholder-white/30"
              />
              <button type="submit"
                      className="px-7 py-3.5 rounded-full gradient-bg font-medium hover:shadow-[0_0_24px_rgba(157,76,221,0.5)] transition-shadow whitespace-nowrap">
                Notify me
              </button>
            </form>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-14 text-xs text-white/30 font-mono"
        >
          hello@weautomate.tech · India
        </motion.div>
      </motion.div>
    </div>
  );
}
